import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button,
  Typography, Alert, CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  login, solicitarRecuperacion, confirmarRecuperacion
} from '../api';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pantallas: 'login' | '2fa' | 'recuperar'
  const [pantalla, setPantalla] = useState<'login' | '2fa' | 'recuperar'>('login');
  const [codigo2fa, setCodigo2fa] = useState('');

  // Recuperación de contraseña
  const [pasoRec, setPasoRec] = useState<1 | 2>(1);
  const [emailRec, setEmailRec] = useState('');
  const [codigoRec, setCodigoRec] = useState('');
  const [passRec1, setPassRec1] = useState('');
  const [passRec2, setPassRec2] = useState('');
  const [msgRec, setMsgRec] = useState<{ tipo: 'success' | 'error' | 'info', texto: string } | null>(null);
  const [cargandoRec, setCargandoRec] = useState(false);

  const entrar = (data: any) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    navigate('/');
  };

  // ── PASO 1: email + contraseña ──
  const handleLogin = async () => {
    if (loading) return;
    if (!email || !password) { setError('Rellena el email y la contraseña'); return; }
    setLoading(true);
    setError('');
    try {
      const data = await login(email, password);
      entrar(data);
    } catch (err: any) {
      const detalle = err.response?.data?.detail;
      if (detalle === '2FA_REQUERIDO') {
        setPantalla('2fa');
        setCodigo2fa('');
      } else {
        setError(detalle || 'Email o contraseña incorrectos');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── PASO 2: código del Authenticator ──
  const handleVerificar = async () => {
    if (loading) return;
    if (codigo2fa.length !== 6) { setError('El código tiene 6 dígitos'); return; }
    setLoading(true);
    setError('');
    try {
      const data = await login(email, password, codigo2fa);
      entrar(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Código incorrecto');
      setCodigo2fa('');
    } finally {
      setLoading(false);
    }
  };

  const volverAlLogin = () => {
    setPantalla('login');
    setCodigo2fa('');
    setPassword('');
    setError('');
  };

  // ── Recuperación de contraseña ──
  const handleSolicitarCodigo = async () => {
    setMsgRec(null);
    setCargandoRec(true);
    try {
      const res = await solicitarRecuperacion(emailRec.trim());
      setMsgRec({ tipo: 'info', texto: res.mensaje });
      setPasoRec(2);
    } catch {
      setMsgRec({ tipo: 'error', texto: 'Error al solicitar el código. Inténtalo de nuevo.' });
    } finally {
      setCargandoRec(false);
    }
  };

  const handleConfirmarCambio = async () => {
    setMsgRec(null);
    if (passRec1 !== passRec2) {
      setMsgRec({ tipo: 'error', texto: 'Las contraseñas no coinciden' });
      return;
    }
    if (passRec1.length < 6) {
      setMsgRec({ tipo: 'error', texto: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }
    setCargandoRec(true);
    try {
      const res = await confirmarRecuperacion(emailRec.trim(), codigoRec.trim(), passRec1);
      setMsgRec({ tipo: 'success', texto: res.mensaje });
      setTimeout(() => {
        setPantalla('login');
        setPasoRec(1);
        setEmail(emailRec.trim());
        setCodigoRec(''); setPassRec1(''); setPassRec2(''); setMsgRec(null);
      }, 2000);
    } catch (e: any) {
      setMsgRec({ tipo: 'error', texto: e.response?.data?.detail || 'Código incorrecto o caducado' });
    } finally {
      setCargandoRec(false);
    }
  };

  const estiloBoton = {
    mt: 3, bgcolor: '#F5C200', color: '#0D0D0D', fontWeight: 700,
    '&:hover': { bgcolor: '#d9ab00' }, borderRadius: 2, py: 1.5
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#0D0D0D',
      backgroundImage: 'radial-gradient(circle at 50% 0%, #1a1a1a 0%, #0D0D0D 70%)',
      px: 2
    }}>
      <Card sx={{ width: '100%', maxWidth: 400, borderRadius: 3,
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        borderTop: '3px solid #F5C200' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img src="/logo_hei.png" alt="HEI" style={{ width: 170, marginBottom: 12 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase', fontSize: 12 }}>
              HEI SIS-Plataforma de gestión
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* ══ PANTALLA LOGIN ══ */}
          {pantalla === 'login' && (
            <>
              <TextField
                fullWidth label="Email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                margin="normal"
              />
              <TextField
                fullWidth label="Contraseña" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                margin="normal"
              />
              <Button fullWidth variant="contained" size="large"
                disabled={loading} onClick={handleLogin} sx={estiloBoton}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
              </Button>

              <Typography
                variant="body2"
                onClick={() => { setPantalla('recuperar'); setEmailRec(email); setMsgRec(null); setError(''); }}
                sx={{ mt: 2, textAlign: 'center', color: 'text.secondary', cursor: 'pointer',
                  '&:hover': { color: '#F5C200', textDecoration: 'underline' } }}
              >
                ¿Has olvidado tu contraseña?
              </Typography>
            </>
          )}

          {/* ══ PANTALLA 2FA ══ */}
          {pantalla === '2fa' && (
            <>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>🔐 Verificación en dos pasos</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Abre Microsoft Authenticator en tu móvil e introduce el código de 6 dígitos.
              </Typography>
              <TextField
                fullWidth label="Código de verificación" value={codigo2fa}
                onChange={(e) => setCodigo2fa(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleVerificar(); }}
                inputProps={{ inputMode: 'numeric', style: { letterSpacing: 8, textAlign: 'center', fontWeight: 700, fontSize: 22 } }}
                margin="normal"
              />
              <Button fullWidth variant="contained" size="large"
                disabled={loading || codigo2fa.length !== 6}
                onClick={handleVerificar} sx={estiloBoton}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verificar y entrar'}
              </Button>
              <Typography
                variant="caption" onClick={volverAlLogin}
                sx={{ display: 'block', mt: 2, textAlign: 'center', color: 'text.secondary',
                  cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                ← Volver
              </Typography>
            </>
          )}

          {/* ══ PANTALLA RECUPERAR CONTRASEÑA ══ */}
          {pantalla === 'recuperar' && (
            <>
              <Typography sx={{ fontWeight: 600, mb: 1.5 }}>🔑 Restablecer contraseña</Typography>
              {msgRec && <Alert severity={msgRec.tipo} sx={{ mb: 2 }}>{msgRec.texto}</Alert>}

              {pasoRec === 1 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Escribe tu email y te enviaremos un código de verificación.
                  </Typography>
                  <TextField fullWidth label="Email" type="email" size="small"
                    value={emailRec} onChange={(e) => setEmailRec(e.target.value)} />
                  <Button fullWidth variant="contained" onClick={handleSolicitarCodigo}
                    disabled={cargandoRec || !emailRec.includes('@')} sx={{ ...estiloBoton, mt: 1.5 }}>
                    {cargandoRec ? <CircularProgress size={22} color="inherit" /> : 'Enviarme el código'}
                  </Button>
                </>
              )}

              {pasoRec === 2 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Introduce el código de 6 dígitos que has recibido por email
                    (caduca en 15 minutos) y tu contraseña nueva.
                  </Typography>
                  <TextField fullWidth label="Código de 6 dígitos" size="small" value={codigoRec}
                    onChange={(e) => setCodigoRec(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ inputMode: 'numeric', style: { letterSpacing: 6, textAlign: 'center', fontWeight: 700 } }}
                    sx={{ mb: 1.5 }} />
                  <TextField fullWidth label="Contraseña nueva" type="password" size="small"
                    value={passRec1} onChange={(e) => setPassRec1(e.target.value)} sx={{ mb: 1.5 }} />
                  <TextField fullWidth label="Repite la contraseña nueva" type="password" size="small"
                    value={passRec2} onChange={(e) => setPassRec2(e.target.value)} />
                  <Button fullWidth variant="contained" onClick={handleConfirmarCambio}
                    disabled={cargandoRec || codigoRec.length !== 6 || !passRec1 || !passRec2}
                    sx={{ ...estiloBoton, mt: 1.5 }}>
                    {cargandoRec ? <CircularProgress size={22} color="inherit" /> : 'Cambiar contraseña'}
                  </Button>
                  <Typography variant="caption" onClick={handleSolicitarCodigo}
                    sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.secondary',
                      cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    ¿No te ha llegado? Reenviar código
                  </Typography>
                </>
              )}

              <Typography
                variant="caption"
                onClick={() => { setPantalla('login'); setPasoRec(1); setMsgRec(null); }}
                sx={{ display: 'block', mt: 1.5, textAlign: 'center', color: 'text.secondary',
                  cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                ← Volver al inicio de sesión
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}